import { Box, Flex, forwardRef, Icon, Text } from "@chakra-ui/react";
import LogRocket from "logrocket";
import React from "react";
import { HiOutlineSparkles } from "react-icons/hi2";

import { useToast } from "../../../../components";
import { useSendGAEvent } from "../../../../utils/googleAnalytics";
import {
  CallSpeakerFragment,
  useUpdateCallSpeakerMutation,
} from "../../../graphql";
import useAiNotesStatus from "../SidebarTabs/Ai/Notes/NotesQA/useAiNotesStatus";
import SpeakerMenu, { SpeakerMenuOption } from "../SpeakerMenu";

const getSpeakerColor = (speaker: CallSpeakerFragment): string => {
  if (speaker.isInterviewer) return "#D6BCFA";
  if (speaker.isCandidate) return "#9EDFC8";
  return "gray.100";
};

const getSpeakerHoverColor = (speaker: CallSpeakerFragment): string => {
  if (speaker.isInterviewer) return "purple.300";
  if (speaker.isCandidate) return "#7DCBA8";
  return "gray.300";
};

type SpeakerTagV2Props = {
  callId?: string;
  speaker: CallSpeakerFragment;
  canChangeSpeaker?: boolean;
  speakerOptions?: SpeakerMenuOption[];
  candidateOnly?: boolean;
  defaultIsOpen?: boolean;
};

const SpeakerTagBaseV2: React.FC<SpeakerTagV2Props> = forwardRef(
  (
    {
      speaker,
      canChangeSpeaker = true,
      candidateOnly = false,
      speakerOptions = [],
      ...rest
    },
    ref
  ) => {
    const isAiInterview = speaker.label === "AI Interviewer";
    const aiInterviewGradient =
      "linear-gradient(142deg, rgba(0, 116, 221, 0.30) 0%, rgba(88.82, 30.43, 255, 0.30) 48%, rgba(150.24, 67.92, 255, 0.30) 100%), white;";
    const background = isAiInterview
      ? aiInterviewGradient
      : getSpeakerColor(speaker);
    const hoveredBackground = isAiInterview
      ? aiInterviewGradient
      : getSpeakerHoverColor(speaker);
    const backgroundStyles = {
      background,
      _hover: {
        background: canChangeSpeaker ? hoveredBackground : background,
        cursor: canChangeSpeaker ? "pointer" : "default",
      },
    };
    return (
      <Flex
        ref={ref}
        direction="row"
        borderRadius="12px"
        fontWeight="500"
        fontSize="xs"
        color="black"
        overflow="hidden"
        height="22px"
        maxW="100%"
        lineHeight="22px"
        ml="-6px"
        opacity={candidateOnly && !speaker.isCandidate ? 0.2 : 1}
        transition="opacity 0.2s ease"
        {...backgroundStyles}
        {...rest}
      >
        <Text
          borderRadius="12px"
          px="2"
          zIndex="2"
          bg="inherit"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
          overflow="hidden"
          display="flex"
          alignItems="center"
          gap="1"
        >
          {isAiInterview && (
            <Icon as={HiOutlineSparkles} boxSize="14px" color="black" />
          )}
          {speaker.label}
        </Text>
        <Text
          bg="gray.900"
          borderRadius="50%"
          marginLeft="-23px"
          marginTop="-2px"
          width="26px"
          height="26px"
          flexShrink="0"
        />
        <Box as="span" px="1">
          {speaker.talkTimePercentage}%
        </Box>
      </Flex>
    );
  }
);

const SpeakerTagV2: React.FC<SpeakerTagV2Props> = ({
  callId,
  candidateOnly,
  speaker,
  canChangeSpeaker = false,
  speakerOptions = [],
  defaultIsOpen,
}) => {
  const toast = useToast();
  const sendGAEvent = useSendGAEvent();

  const [updateCallSpeakerMutation] = useUpdateCallSpeakerMutation({
    onError: () => {
      toast({ status: "error", title: "Error changing speaker" });
    },
  });

  const { checkAiNotesStatus } = useAiNotesStatus();

  const updateCallSpeaker = (speakerOptionId: string): void => {
    if (callId) {
      sendGAEvent("update_speaker", "call_review");
      LogRocket.track("speaker-label-updated");
      updateCallSpeakerMutation({
        variables: {
          callId,
          speakerTag: speaker.speakerTag,
          speakerOptionId,
        },
        onCompleted: () => {
          checkAiNotesStatus(callId);
        },
      });
    }
  };

  return (
    <SpeakerMenu
      trigger={SpeakerTagBaseV2}
      defaultIsOpen={defaultIsOpen}
      disabled={!canChangeSpeaker}
      triggerProps={{
        canChangeSpeaker,
        candidateOnly,
        speaker,
      }}
      speakerOptions={speakerOptions}
      onSelect={(option) => updateCallSpeaker(option.id)}
    />
  );
};

export default React.memo(SpeakerTagV2);
